import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';
import { Home } from './pages/Home';
import { CookieConsent } from './components/ui/CookieConsent';
import { db } from './firebase';
import { doc, onSnapshot } from 'firebase/firestore';

// Lazy loaded routes
const ListingMedia = lazy(() => import('./pages/ListingMedia').then(m => ({ default: m.ListingMedia })));
const BrandMedia = lazy(() => import('./pages/BrandMedia').then(m => ({ default: m.BrandMedia })));
const Contact = lazy(() => import('./pages/Contact').then(m => ({ default: m.Contact })));
const Booking = lazy(() => import('./pages/Booking').then(m => ({ default: m.Booking })));
const Terms = lazy(() => import('./pages/Terms').then(m => ({ default: m.Terms })));
const Privacy = lazy(() => import('./pages/Privacy').then(m => ({ default: m.Privacy })));
const Blog = lazy(() => import('./pages/Blog').then(m => ({ default: m.Blog })));
const BlogPost = lazy(() => import('./pages/BlogPost').then(m => ({ default: m.BlogPost })));
const About = lazy(() => import('./pages/About').then(m => ({ default: m.About })));
const PreShootGuide = lazy(() => import('./pages/PreShootGuide').then(m => ({ default: m.PreShootGuide })));

// Admin Routes
const Login = lazy(() => import('./pages/admin/Login').then(m => ({ default: m.Login })));
const Dashboard = lazy(() => import('./pages/admin/Dashboard').then(m => ({ default: m.Dashboard })));
const Overview = lazy(() => import('./pages/admin/Overview').then(m => ({ default: m.Overview })));
const ManageBlog = lazy(() => import('./pages/admin/ManageBlog').then(m => ({ default: m.ManageBlog })));
const ManagePackages = lazy(() => import('./pages/admin/ManagePackages'));
const ManageTestimonials = lazy(() => import('./pages/admin/ManageTestimonials').then(m => ({ default: m.ManageTestimonials })));
const ManageServiceAreas = lazy(() => import('./pages/admin/ManageServiceAreas'));
const ManageClientLogos = lazy(() => import('./pages/admin/ManageClientLogos').then(m => ({ default: m.ManageClientLogos })));
const ManagePreShootGuide = lazy(() => import('./pages/admin/ManagePreShootGuide').then(m => ({ default: m.ManagePreShootGuide })));
const ManageSiteDesign = lazy(() => import('./pages/admin/ManageSiteDesign'));
const ManageGlobalSettings = lazy(() => import('./pages/admin/ManageGlobalSettings').then(m => ({ default: m.ManageGlobalSettings })));

const LoginRedirect = () => {
  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'siteContent', 'global-settings'), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.loginUrl) {
          window.location.href = data.loginUrl;
        } else {
          window.location.href = 'https://portal.realta.media';
        }
      } else {
        window.location.href = 'https://portal.realta.media';
      }
    });
    return () => unsubscribe();
  }, []);
  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-zinc-400 font-display uppercase tracking-widest text-xs">Redirecting to login portal...</p>
      </div>
    </div>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const App: React.FC = () => {
  return (
    <Router>
      <ScrollToTop />
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <main className="flex-grow relative">
          <Suspense fallback={<div className="min-h-[80vh] flex items-center justify-center bg-black"><div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"></div></div>}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/listing-media" element={<ListingMedia />} />
              <Route path="/brand-media" element={<BrandMedia />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/booking" element={<Booking />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:id" element={<BlogPost />} />
              <Route path="/pre-shoot-guide" element={<PreShootGuide />} />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<Login />} />
              <Route path="/admin" element={<Dashboard />}>
                <Route index element={<Overview />} />
                <Route path="blog" element={<ManageBlog />} />
                <Route path="packages" element={<ManagePackages />} />
                <Route path="design" element={<ManageSiteDesign />} />
                <Route path="testimonials" element={<ManageTestimonials />} />
                <Route path="service-areas" element={<ManageServiceAreas />} />
                <Route path="client-logos" element={<ManageClientLogos />} />
                <Route path="pre-shoot-guide" element={<ManagePreShootGuide />} />
                <Route path="settings" element={<ManageGlobalSettings />} />
              </Route>

              {/* Original Login Redirect */}
              <Route path="/login" element={<LoginRedirect />} />

              <Route path="*" element={<Home />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
        <CookieConsent />
      </div>
    </Router>
  );
};

export default App;